import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { FilterOption } from '@components/ui/FilterMenu';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';

import type { ArAgingPayload, CleanClaimsPayload, RevenueFilters } from './api';
import { type ArResponsibility, ArResponsibilityView } from './ArResponsibilityView';
import { outcomes, rate, share } from './format';
import { revenueQueueUrl } from './navigation';
import { RevenueFiltersBar } from './RevenueFiltersBar';

import './Revenue.css';

type Props = {
  embedded?: boolean;
  view: 'clean' | 'ar';
  clean?: CleanClaimsPayload;
  ar?: ArAgingPayload;
  filters: RevenueFilters;
  patientView: boolean;
  responsibility?: ArResponsibility;
  onResponsibility?: (value: ArResponsibility) => void;
  clinicians?: FilterOption[];
  cliniciansLoading?: boolean;
  loading?: boolean;
  error?: boolean;
  onRetry: () => void;
  onView: (view: 'clean' | 'ar') => void;
  onPatientView: (patient: boolean) => void;
  onFilters: (patch: Partial<RevenueFilters>) => void;
  onAction?: (action: string) => void;
};

export function RevenueDetail({
  embedded = false,
  view,
  clean,
  ar,
  filters,
  patientView,
  responsibility,
  onResponsibility,
  clinicians,
  cliniciansLoading,
  loading,
  error,
  onRetry,
  onView,
  onPatientView,
  onFilters,
  onAction,
}: Props) {
  const [methodology, setMethodology] = useState(false);
  const data = view === 'clean' ? clean : ar;
  const counts = clean?.counts;

  return (
    <div className={`rw rw-product-detail${embedded ? ' rw-embedded' : ''}`}>
      {!embedded && (
        <header className="rw-header">
          <div>
            <div className="rw-breadcrumb">
              <Link to="/jotbill">JotBill home</Link> <span>/</span> Revenue analytics
            </div>
            <h1>{view === 'clean' ? 'Clean claims' : 'Accounts receivable'}</h1>
            <p>Claim outcomes and outstanding balances, with the records behind every number.</p>
          </div>
          <button
            className="rw-button"
            onClick={() => {
              setMethodology(true);
              onAction?.('methodology');
            }}
          >
            <InfoOutlined fontSize="small" /> How we measure
          </button>
        </header>
      )}
      {embedded && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Tabs
            value={view}
            onChange={(_, next) => onView(next)}
            aria-label="Revenue analytics areas"
          >
            <Tab value="clean" label="Clean claims" sx={{ textTransform: 'none' }} />
            <Tab value="ar" label="Accounts receivable" sx={{ textTransform: 'none' }} />
          </Tabs>
          <Button
            startIcon={<InfoOutlined />}
            onClick={() => {
              setMethodology(true);
              onAction?.('methodology');
            }}
          >
            How we measure
          </Button>
        </Box>
      )}
      {!embedded && (
        <nav className="rw-nav" aria-label="Revenue analytics areas">
          <button
            aria-current={view === 'clean' ? 'page' : undefined}
            className={view === 'clean' ? 'active' : ''}
            onClick={() => onView('clean')}
          >
            Clean claims <span>Understand outcomes</span>
          </button>
          <button
            aria-current={view === 'ar' ? 'page' : undefined}
            className={view === 'ar' ? 'active' : ''}
            onClick={() => onView('ar')}
          >
            Accounts receivable <span>Prioritize follow-up</span>
          </button>
        </nav>
      )}
      <main aria-busy={loading}>
        <div className="rw-title-row">
          <div>
            <h2>
              {view === 'clean'
                ? 'From submission to payment'
                : 'Every outstanding dollar, in context'}
            </h2>
            <p>
              {view === 'clean'
                ? 'Start with payer responses. Then isolate the claims that need a closer look.'
                : 'One view of posted insurance and patient responsibility.'}
            </p>
          </div>
        </div>
        <div className="rw-revenue-filters">
          {view === 'clean' && (
            <RevenueFiltersBar
              filters={filters}
              showDates={view === 'clean'}
              dateFrom={
                filters.dateFrom ??
                clean?.date_from ??
                new Date(Date.now() - 89 * 86400000).toISOString().slice(0, 10)
              }
              dateTo={filters.dateTo ?? clean?.date_to ?? new Date().toISOString().slice(0, 10)}
              clinicians={
                clinicians ??
                (data?.clinicians ?? []).map(c => ({ value: c.provider_id, label: c.display_name }))
              }
              cliniciansLoading={cliniciansLoading}
              onFilters={onFilters}
            />
          )}
        </div>
        {(filters.payerName || filters.payerId) && view === 'clean' && (
          <div className="rw-scope-chip">
            <span>
              Claim list focused on <strong>{filters.payerName ?? filters.payerId}</strong>
            </span>
            <button onClick={() => onFilters({ payerId: undefined, payerName: undefined })}>
              Show all payers ×
            </button>
          </div>
        )}
        {!loading && data && (
          <p className="rw-cohort-note">
            {view === 'clean'
              ? 'Summary: selected submission window and clinician, across all payers.'
              : 'Practice-wide posted A/R, separated by who owes the balance.'}
          </p>
        )}
        {loading ? (
          <div className="rw-loading" role="status">
            Loading analytics…
          </div>
        ) : error ? (
          <Alert severity="error" action={<Button onClick={onRetry}>Retry</Button>}>
            Revenue analytics could not load. Try again.
          </Alert>
        ) : data ? (
          <>
            {view === 'clean' && clean && (
              <>
                <div className="rw-summary">
                  <section className="rw-lead">
                    <div className="rw-eyebrow">Paid without full denial</div>
                    <div className="rw-big">{rate(counts?.clean_rate_pct)}</div>
                    <p>
                      <strong>
                        {counts?.clean ?? 0} of {counts?.responded ?? 0}
                      </strong>{' '}
                      responded claims
                    </p>
                    <span className="rw-footnote">
                      Responses to claims submitted in this window; pending excluded.
                    </span>
                  </section>
                  <section>
                    <div className="rw-eyebrow">Full denials</div>
                    <div className="rw-stat">
                      {counts?.denied ?? 0}
                      <span>claims</span>
                    </div>
                    <p>Payer reported a full denial</p>
                    <Link
                      className="rw-link"
                      to={revenueQueueUrl('denials', filters)}
                      onClick={() => onAction?.('inspect_denials')}
                    >
                      Inspect denials <ArrowForwardRounded />
                    </Link>
                    <span className="rw-footnote">Current denied claims in Claims to fix.</span>
                  </section>
                  <section>
                    <div className="rw-eyebrow">Awaiting response</div>
                    <div className="rw-stat">
                      {counts?.pending ?? 0}
                      <span>claims</span>
                    </div>
                    <p>Excluded from the clean rate</p>
                    <Link
                      className="rw-link"
                      to={revenueQueueUrl('pending', filters)}
                      onClick={() => onAction?.('inspect_pending')}
                    >
                      Inspect pending <ArrowForwardRounded />
                    </Link>
                    <span className="rw-footnote">
                      Awaiting payer queue · 30+ days without a response.
                    </span>
                  </section>
                  <section>
                    <div className="rw-eyebrow">Submission speed</div>
                    <div className="rw-stat">
                      {clean.lag.median_days ?? '—'}
                      <span>days median</span>
                    </div>
                    <p>
                      {share(clean.lag.within_3_days, clean.lag.eligible)} sent within 3 calendar
                      days
                    </p>
                    <span className="rw-footnote">
                      {clean.lag.eligible ?? 0} claims with measurable lag
                    </span>
                  </section>
                </div>
                <div className="rw-two-col">
                  <section className="rw-panel">
                    <div className="rw-panel-head">
                      <div>
                        <h3>Where claims land</h3>
                        <p>Payer responses for the selected submission window.</p>
                      </div>
                      <span className="rw-small-label">
                        {counts?.submitted ?? 0} submitted claims
                      </span>
                    </div>
                    <div className="rw-outcome-track">
                      {outcomes.map(
                        (o, i) =>
                          (counts?.[o.key] ?? 0) > 0 && (
                            <Box
                              component="button"
                              key={o.key}
                              aria-label={`${o.label}: ${counts?.[o.key]} claims`}
                              aria-pressed={filters.outcome === o.key}
                              className={`tone-${i}`}
                              sx={{ flex: counts?.[o.key] }}
                              onClick={() =>
                                onFilters({
                                  outcome: filters.outcome === o.key ? undefined : o.key,
                                })
                              }
                            >
                              {counts?.[o.key]}
                            </Box>
                          ),
                      )}
                    </div>
                    <div className="rw-outcome-legend">
                      {outcomes.map((o, i) => (
                        <button
                          key={o.key}
                          className={filters.outcome === o.key ? 'selected' : ''}
                          aria-pressed={filters.outcome === o.key}
                          onClick={() =>
                            onFilters({ outcome: filters.outcome === o.key ? undefined : o.key })
                          }
                        >
                          <span className={`rw-key tone-${i}`} />
                          {o.label}
                          <strong>{counts?.[o.key] ?? 0}</strong>
                        </button>
                      ))}
                    </div>
                  </section>
                  <section className="rw-panel">
                    <div className="rw-panel-head">
                      <div>
                        <h3>Time before submission</h3>
                        <p>
                          Service date → sent date · {clean.lag.p90_days ?? '—'} days at the 90th
                          percentile
                        </p>
                      </div>
                    </div>
                    <div className="rw-lag">
                      {(clean.lag.buckets ?? []).map((b, i) => (
                        <button
                          key={b.label}
                          aria-pressed={filters.lagBucket === i}
                          onClick={() =>
                            onFilters({ lagBucket: filters.lagBucket === i ? undefined : i })
                          }
                        >
                          <strong>{b.count}</strong>
                          <div className="rw-lag-column">
                            <Box
                              component="span"
                              sx={{
                                height: `${(100 * b.count) / Math.max(1, ...(clean.lag.buckets ?? []).map(row => row.count))}%`,
                              }}
                            />
                          </div>
                          <span>{b.label}</span>
                        </button>
                      ))}
                    </div>
                  </section>
                </div>
                <section className="rw-panel">
                  <div className="rw-panel-head">
                    <div>
                      <h3>Which payer needs attention?</h3>
                      <p>Compare payer outcomes for the selected submission window.</p>
                    </div>
                    <button
                      className="rw-text-button"
                      onClick={() => onFilters({ payerId: undefined, payerName: undefined })}
                    >
                      All payers
                    </button>
                  </div>
                  <div className="rw-table-wrap">
                    <table aria-label="Payer outcomes">
                      <thead>
                        <tr>
                          <th>Payer</th>
                          <th>Responded</th>
                          <th>Paid without full denial</th>
                          <th>Full denials</th>
                          <th>Pending</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(clean.payers ?? []).map(p => (
                          <tr key={`${p.payer_id}:${p.payer_name}`}>
                            <th>
                              <button
                                onClick={() =>
                                  onFilters({
                                    payerId: p.payer_id ?? undefined,
                                    payerName: p.payer_name,
                                  })
                                }
                              >
                                {p.payer_name}
                              </button>
                            </th>
                            <td>{p.responded ?? 0}</td>
                            <td>
                              <span className="rw-rate">
                                <Box component="span" sx={{ width: `${p.clean_rate_pct ?? 0}%` }} />
                              </span>
                              <strong>{rate(p.clean_rate_pct)}</strong>
                            </td>
                            <td>{p.denied ?? 0}</td>
                            <td>{p.pending ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
            {view === 'ar' && ar && (
              <ArResponsibilityView
                data={ar}
                responsibility={responsibility ?? (patientView ? 'patient' : 'all')}
                onResponsibility={onResponsibility ?? (value => onPatientView(value === 'patient'))}
              />
            )}
          </>
        ) : null}
      </main>
      <Dialog open={methodology} onClose={() => setMethodology(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          How we measure{' '}
          <IconButton aria-label="Close methodology" onClick={() => setMethodology(false)}>
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <h3>{view === 'clean' ? 'Clean claims' : 'Accounts receivable'}</h3>
          <p>{data?.definition}</p>
          {clean && view === 'clean' && <p>{clean.cohort_description}</p>}
          <p>
            This is not a first-pass clean-claim rate. Operational queues show current actionable
            claims; their membership can differ from historical analytics cohorts.
          </p>
          <p>
            Insurance and patient A/R use disjoint ledger responsibility balances. Unknown ages are
            excluded from older-than-90-day totals. Aging is not a payer-specific timely-filing
            deadline.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
